// src/pages/auth/ResetPasswordConfirm.jsx
import React, { useState, useEffect } from 'react';
import { Form, Alert } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../services/api';
import '../../styles/login.css';

const ResetPasswordConfirm = () => {
  const { uid, token } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Validate that we have uid and token
    if (!uid || !token) {
      setError('Invalid password reset link. Please request a new one.');
    }
  }, [uid, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Client-side validation
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post(
        '/auth/password/reset/confirm/',
        {
          uid,
          token,
          new_password1: newPassword,
          new_password2: confirmPassword,
        }
      );

      if (response.status === 200) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      let errorMessage = 'Failed to reset password. ';

      if (err.response?.data) {
        const data = err.response.data;
        // Handle various error formats
        if (data.token) {
          errorMessage += 'The reset link is invalid or has expired. Please request a new one.';
        } else if (data.uid) {
          errorMessage += 'Invalid reset link.';
        } else if (data.new_password2 || data.new_password1) {
          errorMessage += Array.isArray(data.new_password2)
            ? data.new_password2.join(' ')
            : data.new_password1?.join(' ') || 'Password validation failed.';
        } else if (data.detail) {
          errorMessage += data.detail;
        } else {
          errorMessage += 'Please try again or request a new reset link.';
        }
      } else {
        errorMessage += 'Please try again.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-form">
      <h2>Set New Password</h2>
      {email && (
        <p className="text-muted mb-4">
          Resetting password for: <strong>{email}</strong>
        </p>
      )}

      {success && (
        <Alert variant="success">
          <strong>Password reset successful!</strong>
          <br />
          Redirecting to login...
        </Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {!success && (
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="formNewPassword">
            <Form.Label>New Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={loading || !uid || !token}
              minLength={8}
            />
            <Form.Text className="text-muted">
              Must be at least 8 characters long.
            </Form.Text>
          </Form.Group>

          <Form.Group controlId="formConfirmPassword" className="mt-3">
            <Form.Label>Confirm New Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading || !uid || !token}
              minLength={8}
            />
          </Form.Group>

          <button
            type="submit"
            disabled={loading || !newPassword || !confirmPassword || !uid || !token}
            className="ff-btn ff-btn--primary ff-btn--block mt-4"
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </Form>
      )}

      {!success && (
        <div className="signup-redirect mt-3 text-center">
          <span>
            Remember your password?{' '}
            <span className="signup-link" onClick={() => navigate('/login')} role="button">
              Back to Login
            </span>
          </span>
        </div>
      )}

      {success && (
        <div className="text-center mt-3">
          <button type="button" className="ff-btn ff-btn--link" onClick={() => navigate('/login')}>
            Go to Login Now
          </button>
        </div>
      )}
    </div>
  );
};

export default ResetPasswordConfirm;
